// src/pages/inventory/realisasi/RealisasiStokBahanPG4.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, set } from "firebase/database";

// Import XLSX untuk handling file Excel
import * as XLSX from "xlsx";

// Import formatters dari file utilitas
import {
    excelDateToDDMMYYYY,
    formatNumber,
    formatText
} from "../../../utils/formatters-master";

// Import fungsi formatLastUpdated dari file terpisah
import { formatLastUpdated } from "../../../utils/formatLastUpdated";

// Import CustomTableContainer dan CustomBreadcrumbs dari komponen terpisah
import CustomTableContainer from "../../../components/CustomScrollContainer";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";

import RimenderStock from "../rimender-stock/RimenderStock";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cek apakah nilai harus disimpan (tidak kosong dan bukan 0 atau tanda -)
const shouldSaveValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return false;
    }
    
    // Jika berupa string, cek apakah kosong atau "0" atau "-"
    if (typeof value === 'string') {
        const trimmed = value.trim();
        
        // Cek berbagai format kosong/nol
        const isEmptyOrZero = trimmed === '' || 
                            trimmed === '0' || 
                            trimmed === '0.0' || 
                            trimmed === '0,0' ||
                            trimmed === '0.00' ||
                            trimmed === '0,00' ||
                            trimmed === '-' ||
                            trimmed === ' - ' ||
                            trimmed === '--' ||
                            trimmed === 'N/A' ||
                            trimmed === 'n/a' ||
                            trimmed.toLowerCase() === 'null' ||
                            trimmed.toLowerCase() === 'undefined';
        
        return !isEmptyOrZero;
    }
    
    // Jika berupa number, cek apakah bukan 0
    if (typeof value === 'number') {
        return value !== 0 && !isNaN(value);
    }
    
    // Untuk tipe data lain, simpan
    return true;
};

// Helper: Parse tanggal dari berbagai format ke DD-MM-YYYY
const parseDateToDDMMYYYY = (dateStr) => {
    if (!dateStr || dateStr === '') {
        console.log("dateStr kosong atau null");
        return null;
    }
    
    const normalizedStr = String(dateStr).trim();
    console.log("normalizedStr:", normalizedStr);
    
    // Skip jika adalah tanda -
    if (normalizedStr === '-' || normalizedStr === '--') {
        console.log("Skip karena tanda -");
        return null;
    }
    
    // Format DD/MM/YYYY (contoh: 01/12/2025)
    const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const slashMatch = normalizedStr.match(slashPattern);
    if (slashMatch) {
        const [_, day, month, year] = slashMatch;
        console.log("Match format DD/MM/YYYY");
        return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
    }
    
    // Format DD-MM-YYYY (sudah benar)
    const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const dashMatch = normalizedStr.match(dashPattern);
    if (dashMatch) {
        const [_, day, month, year] = dashMatch;
        console.log("Match format DD-MM-YYYY");
        return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
    }
    
    // Coba parse dengan Date object (untuk format Excel serial date)
    try {
        console.log("Mencoba parse dengan excelDateToDDMMYYYY...");
        const excelDate = excelDateToDDMMYYYY(dateStr);
        console.log("Hasil excelDateToDDMMYYYY:", excelDate);
        if (excelDate && excelDate !== '-') {
            // Format excelDateToDDMMYYYY mengembalikan DD/MM/YYYY atau DD-MM-YYYY
            // Cek format dan konversi ke DD-MM-YYYY jika perlu
            
            // Jika sudah DD-MM-YYYY, langsung return
            const dashPattern = /^\d{2}-\d{2}-\d{4}$/;
            if (dashPattern.test(excelDate)) {
                console.log("Sudah format DD-MM-YYYY");
                return excelDate;
            }
            
            // Jika format DD/MM/YYYY, konversi ke DD-MM-YYYY
            const slashPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
            const slashMatch = excelDate.match(slashPattern);
            if (slashMatch) {
                const [_, day, month, year] = slashMatch;
                console.log("Konversi dari DD/MM/YYYY ke DD-MM-YYYY");
                return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
            }
            
            // Return apa adanya jika sudah valid
            console.log("Menggunakan hasil dari excelDateToDDMMYYYY:", excelDate);
            return excelDate;
        }
    } catch (e) {
        console.warn("Gagal parse tanggal dengan excelDateToDDMMYYYY:", e);
    }
    
    // Coba parse sebagai Date object biasa (HANYA untuk string yang bukan number besar)
    try {
        // Skip jika adalah number Excel serial date (lebih dari 10000 biasanya)
        if (!isNaN(normalizedStr) && parseInt(normalizedStr) > 10000) {
            console.log("Skip parse sebagai Date object karena kemungkinan Excel serial date:", normalizedStr);
            return null;
        }
        
        console.log("Mencoba parse sebagai Date object...");
        const dateObj = new Date(normalizedStr);
        if (!isNaN(dateObj.getTime())) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            console.log("Berhasil parse sebagai Date object");
            return `${day}-${month}-${year}`;
        }
    } catch (e) {
        console.warn("Gagal parse sebagai Date object:", e);
    }
    
    console.log("Tidak ada format yang cocok");
    return null;
};

// Fungsi untuk import Excel ke Firebase untuk realisasi stok bahan PG4
const importExcelToFirebaseRealisasiStokBahan = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[1];
                const worksheet = workbook.Sheets[sheetName];
                
                // Gunakan raw: true untuk mendapatkan nilai mentah
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                    header: 1, 
                    raw: true,
                    defval: '' 
                });
                
                console.log("=== DEBUG EXCEL STRUCTURE ===");
                console.log("SheetNames:", workbook.SheetNames);
                console.log("Menggunakan sheet:", sheetName);
                console.log("Total rows in jsonData:", jsonData.length);
                
                // Ambil tanggal update dari C3 (baris 2, kolom 2, 0-based index)
                let tanggalUpdate = '';
                if (jsonData.length > 2 && jsonData[2].length > 2) {
                    const rawTanggal = jsonData[2][2]; // C3
                    console.log("Nilai raw C3 dari jsonData[2][2]:", rawTanggal, `(type: ${typeof rawTanggal})`);
                    
                    tanggalUpdate = parseDateToDDMMYYYY(rawTanggal);
                    console.log("Hasil parse tanggal dari C3:", rawTanggal, "->", tanggalUpdate);
                    
                    // Jika masih null, coba dari worksheet langsung
                    const cellC3 = worksheet['C3'];
                    if (!tanggalUpdate && cellC3 && cellC3.v) {
                        console.log("Coba parse dari worksheet['C3'].v:", cellC3.v);
                        tanggalUpdate = parseDateToDDMMYYYY(cellC3.v);
                        console.log("Hasil parse dari worksheet['C3'].v:", cellC3.v, "->", tanggalUpdate);
                    }
                }
                
                // Jika tanggal masih tidak valid, cari di sel sekitar
                if (!tanggalUpdate || tanggalUpdate === '-') {
                    console.log("=== MENCARI TANGGAL DI SEL SEKITAR C3 ===");
                    
                    const potentialDateCells = ['B3', 'D3', 'C2', 'C4', 'B2', 'D2', 'A3', 'E3'];
                    
                    for (const cellRef of potentialDateCells) {
                        const cell = worksheet[cellRef];
                        if (cell && cell.v) {
                            console.log(`Cek ${cellRef}:`, cell.v, `(type: ${typeof cell.v})`);
                            const parsed = parseDateToDDMMYYYY(String(cell.v));
                            if (parsed) {
                                tanggalUpdate = parsed;
                                console.log(`Tanggal ditemukan di ${cellRef}:`, tanggalUpdate);
                                break;
                            }
                        }
                    }
                }
                
                // Fallback: gunakan tanggal hari ini
                if (!tanggalUpdate || tanggalUpdate === '-') {
                    console.log("Menggunakan tanggal hari ini sebagai fallback");
                    const today = new Date();
                    const day = String(today.getDate()).padStart(2, '0');
                    const month = String(today.getMonth() + 1).padStart(2, '0');
                    const year = today.getFullYear();
                    tanggalUpdate = `${day}-${month}-${year}`;
                    console.log("Tanggal update (fallback):", tanggalUpdate);
                }
                
                // Validasi format DD-MM-YYYY
                const datePattern = /^\d{2}-\d{2}-\d{4}$/;
                if (!datePattern.test(tanggalUpdate)) {
                    throw new Error(`Format tanggal tidak valid: "${tanggalUpdate}". Harus DD-MM-YYYY. Pastikan sel C3 berisi tanggal yang valid.`);
                }
                
                console.log("=== TANGGAL UPDATE FINAL ===");
                console.log("Tanggal yang akan digunakan:", tanggalUpdate);
                
                const dataStartRow = 7; // B8 (0-based index: 7)
                
                // Mapping kolom berdasarkan spesifikasi
                const columnMapping = {
                    item_code: 0,      // Kolom B (index 0)
                    
                    // Realisasi Data (C-H)
                    fresh_pineapple: 1,           // C
                    banana: 2,                    // D
                    other_fresh_fruits: 3,        // E
                    rnd_fresh_pine: 4,           // F
                    rnd_banana: 5,               // G
                    rnd_other_fresh_fruits: 6,   // H
                    
                    // Forecast Weekly (I-Q)
                    forecast_weekly_01: 7,        // I
                    forecast_weekly_02: 8,        // J
                    forecast_weekly_03: 9,        // K
                    forecast_weekly_04: 10,       // L
                    forecast_weekly_05: 11,       // M
                    forecast_weekly_06: 12,       // N
                    forecast_weekly_07: 13,       // O
                    forecast_weekly_08: 14,       // P
                    forecast_weekly_09: 15,       // Q
                    
                    // Stock PG4 (R-W)
                    warehouse_bpp: 16,            // R
                    gd_mixer_fresh_pine: 17,      // S
                    gd_mat_banana: 18,            // T
                    gd_mat_other_fruits: 19,      // U
                    quality_insp: 20,             // V
                    transit_and_transfer: 21      // W
                };

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let skippedCount = 0;
                let ignoredCount = 0;
                let errorCount = 0;

                // Helper untuk parsing angka
                const parseNumberForFirebase = (value) => {
                    if (!shouldSaveValue(value)) {
                        return null;
                    }
                    
                    try {
                        // Jika sudah string
                        if (typeof value === 'string') {
                            const trimmed = value.trim();
                            
                            if (trimmed === '' || trimmed === '-' || trimmed === '--' || 
                                trimmed === '0' || trimmed === '0.0' || trimmed === '0.00') {
                                return null;
                            }
                            
                            // Jika sudah format yang diinginkan
                            const decimalPattern = /^\d+\.\d{4}$/;
                            if (decimalPattern.test(trimmed)) {
                                return trimmed;
                            }
                            
                            // Process nilai
                            let processedValue = trimmed;
                            processedValue = processedValue.replace(/\./g, '');
                            processedValue = processedValue.replace(/,/g, '.');
                            
                            const num = parseFloat(processedValue);
                            
                            if (!isNaN(num) && num !== 0) {
                                const formatted = (Math.round(num * 10000) / 10000).toFixed(4);
                                return formatted;
                            }
                        }
                        
                        // Jika number langsung
                        if (typeof value === 'number') {
                            if (value === 0 || isNaN(value)) {
                                return null;
                            }
                            const formatted = (Math.round(value * 10000) / 10000).toFixed(4);
                            return formatted;
                        }
                        
                        return null;
                    } catch (error) {
                        console.warn("Gagal parse number:", value, error);
                        return null;
                    }
                };

                // Proses data dari baris 8 (B8) ke bawah
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const itemCode = row[columnMapping.item_code];
                    
                    // Skip jika item_code kosong, 0, atau tanda -
                    if (!shouldSaveValue(itemCode)) {
                        ignoredCount++;
                        continue;
                    }

                    const formattedItemCode = String(itemCode).trim();
                    
                    // Helper untuk menambahkan data
                    const addIfNeeded = (obj, key, value) => {
                        const parsedValue = parseNumberForFirebase(value);
                        if (parsedValue !== null) {
                            obj[key] = parsedValue;
                            return true;
                        }
                        return false;
                    };
                    
                    // Cek apakah ada data yang valid
                    const hasAnyData = () => {
                        // Cek semua kolom dari C (index 1) sampai W (index 21)
                        for (let col = 1; col <= 21; col++) {
                            const value = row[col];
                            if (shouldSaveValue(value)) {
                                return true;
                            }
                        }
                        return false;
                    };
                    
                    if (!hasAnyData()) {
                        console.log(`Skip baris ${i + 1} (${formattedItemCode}): Semua data kosong/0/-`);
                        skippedCount++;
                        continue;
                    }
                    
                    try {
                        // SOLUSI: Pastikan root item memiliki struktur yang benar
                        const rootItemRef = ref(database, `db_inventory/db_stok_bahan/${formattedItemCode}`);
                        
                        // Cek apakah root item sudah ada
                        const rootItemSnapshot = await onValuePromise(rootItemRef);
                        const existingRootData = rootItemSnapshot.val() || {};
                        
                        // Update atau buat root item dengan struktur yang benar
                        const updatedRootData = {
                            ...existingRootData,
                            item_code: formattedItemCode,
                            // Pastikan ada struktur yang diperlukan
                            realisasi: existingRootData.realisasi || {},
                            forecast_weekly: existingRootData.forecast_weekly || {},
                            stock_PG4: existingRootData.stock_PG4 || {}
                        };
                        
                        // Simpan data root item
                        await set(rootItemRef, updatedRootData);
                        
                        // 1. Simpan data realisasi
                        const realisasiData = {
                            tanggal_update: tanggalUpdate,
                            last_imported_at: importTimestamp
                        };
                        
                        const realisasiValues = {};
                        let hasRealisasiData = false;
                        
                        hasRealisasiData = addIfNeeded(realisasiValues, 'fresh_pineapple', row[columnMapping.fresh_pineapple]) || hasRealisasiData;
                        hasRealisasiData = addIfNeeded(realisasiValues, 'banana', row[columnMapping.banana]) || hasRealisasiData;
                        hasRealisasiData = addIfNeeded(realisasiValues, 'other_fresh_fruits', row[columnMapping.other_fresh_fruits]) || hasRealisasiData;
                        hasRealisasiData = addIfNeeded(realisasiValues, 'rnd_fresh_pine', row[columnMapping.rnd_fresh_pine]) || hasRealisasiData;
                        hasRealisasiData = addIfNeeded(realisasiValues, 'rnd_banana', row[columnMapping.rnd_banana]) || hasRealisasiData;
                        hasRealisasiData = addIfNeeded(realisasiValues, 'rnd_other_fresh_fruits', row[columnMapping.rnd_other_fresh_fruits]) || hasRealisasiData;
                        
                        if (hasRealisasiData) {
                            realisasiData.values = realisasiValues;
                            const realisasiRef = ref(database, `db_inventory/db_stok_bahan/${formattedItemCode}/realisasi/${tanggalUpdate}`);
                            await set(realisasiRef, realisasiData);
                        }
                        
                        // 2. Simpan data forecast_weekly
                        const forecastWeeklyData = {
                            tanggal_update: tanggalUpdate,
                            last_imported_at: importTimestamp
                        };
                        
                        const forecastValues = {};
                        let hasForecastData = false;
                        
                        hasForecastData = addIfNeeded(forecastValues, '01', row[columnMapping.forecast_weekly_01]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '02', row[columnMapping.forecast_weekly_02]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '03', row[columnMapping.forecast_weekly_03]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '04', row[columnMapping.forecast_weekly_04]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '05', row[columnMapping.forecast_weekly_05]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '06', row[columnMapping.forecast_weekly_06]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '07', row[columnMapping.forecast_weekly_07]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '08', row[columnMapping.forecast_weekly_08]) || hasForecastData;
                        hasForecastData = addIfNeeded(forecastValues, '09', row[columnMapping.forecast_weekly_09]) || hasForecastData;
                        
                        if (hasForecastData) {
                            forecastWeeklyData.values = forecastValues;
                            const forecastRef = ref(database, `db_inventory/db_stok_bahan/${formattedItemCode}/forecast_weekly/${tanggalUpdate}`);
                            await set(forecastRef, forecastWeeklyData);
                        }
                        
                        // 3. Simpan data stock_PG4
                        const stockPG4Data = {
                            tanggal_update: tanggalUpdate,
                            last_imported_at: importTimestamp
                        };
                        
                        const stockValues = {};
                        let hasStockData = false;
                        
                        hasStockData = addIfNeeded(stockValues, 'warehouse_bpp', row[columnMapping.warehouse_bpp]) || hasStockData;
                        hasStockData = addIfNeeded(stockValues, 'gd_mixer_fresh_pine', row[columnMapping.gd_mixer_fresh_pine]) || hasStockData;
                        hasStockData = addIfNeeded(stockValues, 'gd_mat_banana', row[columnMapping.gd_mat_banana]) || hasStockData;
                        hasStockData = addIfNeeded(stockValues, 'gd_mat_other_fruits', row[columnMapping.gd_mat_other_fruits]) || hasStockData;
                        hasStockData = addIfNeeded(stockValues, 'quality_insp', row[columnMapping.quality_insp]) || hasStockData;
                        hasStockData = addIfNeeded(stockValues, 'transit_and_transfer', row[columnMapping.transit_and_transfer]) || hasStockData;
                        
                        if (hasStockData) {
                            stockPG4Data.values = stockValues;
                            const stockRef = ref(database, `db_inventory/db_stok_bahan/${formattedItemCode}/stock_PG4/${tanggalUpdate}`);
                            await set(stockRef, stockPG4Data);
                        }
                        
                        // Cek apakah item sudah ada
                        const stokRef = ref(database, "db_inventory/db_stok_bahan");
                        const existingStokSnapshot = await onValuePromise(stokRef);
                        const existingStokData = existingStokSnapshot.val() || {};
                        
                        const existingItemData = existingStokData[formattedItemCode];
                        const hasExistingData = existingItemData && (
                            (existingItemData.realisasi && existingItemData.realisasi[tanggalUpdate]) ||
                            (existingItemData.forecast_weekly && existingItemData.forecast_weekly[tanggalUpdate]) ||
                            (existingItemData.stock_PG4 && existingItemData.stock_PG4[tanggalUpdate])
                        );
                        
                        if (hasExistingData) {
                            updatedCount++;
                        } else {
                            importedCount++;
                        }
                        
                        // Debug log untuk beberapa data pertama
                        if (importedCount + updatedCount < 2) {
                            console.log(`Data baris ${i + 1} (${formattedItemCode}):`);
                            console.log(`- Realisasi:`, realisasiData);
                            console.log(`- Forecast Weekly:`, forecastWeeklyData);
                            console.log(`- Stock PG4:`, stockPG4Data);
                        }
                        
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        errorCount++;
                    }
                }

                console.log(`=== HASIL IMPORT ===`);
                console.log(`Baru: ${importedCount}`);
                console.log(`Update: ${updatedCount}`);
                console.log(`Skip (kosong/0/-): ${skippedCount}`);
                console.log(`Abaikan: ${ignoredCount}`);
                console.log(`Error: ${errorCount}`);
                console.log(`Total: ${importedCount + updatedCount}`);
                console.log(`Tanggal Update: ${tanggalUpdate}`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    skippedCount,
                    ignoredCount,
                    errorCount,
                    total: importedCount + updatedCount,
                    tanggalUpdate
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

// Fungsi untuk refresh data setelah import
const refreshRealisasiStokBahanData = async (database, onValuePromise) => {
    const stokRef = ref(database, "db_inventory/db_stok_bahan");
    const updatedStokSnapshot = await onValuePromise(stokRef);
    const updatedStokData = updatedStokSnapshot.val() || {};
    
    const updatedLoadedData = [];
    
    // Flatten data untuk ditampilkan dalam tabel
    Object.keys(updatedStokData).forEach(itemCode => {
        const itemData = updatedStokData[itemCode];
        
        if (!itemData || typeof itemData !== 'object') {
            return;
        }
        
        const rootItemCode = itemData.item_code || itemCode;
        
        // Gabungkan data dari realisasi, forecast_weekly, dan stock_PG4
        const allNodes = {};
        
        // Kumpulkan semua tanggal dari ketiga sumber
        const collectDates = (source) => {
            if (source) {
                Object.keys(source).forEach(tanggalKey => {
                    if (!allNodes[tanggalKey]) {
                        allNodes[tanggalKey] = {
                            tanggal_update: tanggalKey,
                            item_code: rootItemCode,
                            firebaseKey: `${itemCode}_${tanggalKey}`
                        };
                    }
                });
            }
        };
        
        collectDates(itemData.realisasi);
        collectDates(itemData.forecast_weekly);
        collectDates(itemData.stock_PG4);
        
        // Gabungkan data untuk setiap tanggal
        Object.keys(allNodes).forEach(tanggalKey => {
            const node = allNodes[tanggalKey];
            
            // Data dari realisasi
            const realisasiNode = itemData.realisasi?.[tanggalKey];
            if (realisasiNode) {
                node.realisasi_fresh_pineapple = realisasiNode.values?.fresh_pineapple || '';
                node.realisasi_banana = realisasiNode.values?.banana || '';
                node.realisasi_other_fresh_fruits = realisasiNode.values?.other_fresh_fruits || '';
                node.realisasi_rnd_fresh_pine = realisasiNode.values?.rnd_fresh_pine || '';
                node.realisasi_rnd_banana = realisasiNode.values?.rnd_banana || '';
                node.realisasi_rnd_other_fresh_fruits = realisasiNode.values?.rnd_other_fresh_fruits || '';
                node.last_imported_at = realisasiNode.last_imported_at || '';
            }
            
            // Data dari forecast_weekly
            const forecastNode = itemData.forecast_weekly?.[tanggalKey];
            if (forecastNode) {
                node.forecast_weekly_01 = forecastNode.values?.['01'] || '';
                node.forecast_weekly_02 = forecastNode.values?.['02'] || '';
                node.forecast_weekly_03 = forecastNode.values?.['03'] || '';
                node.forecast_weekly_04 = forecastNode.values?.['04'] || '';
                node.forecast_weekly_05 = forecastNode.values?.['05'] || '';
                node.forecast_weekly_06 = forecastNode.values?.['06'] || '';
                node.forecast_weekly_07 = forecastNode.values?.['07'] || '';
                node.forecast_weekly_08 = forecastNode.values?.['08'] || '';
                node.forecast_weekly_09 = forecastNode.values?.['09'] || '';
                
                if (!node.last_imported_at) {
                    node.last_imported_at = forecastNode.last_imported_at || '';
                }
            }
            
            // Data dari stock_PG4
            const stockNode = itemData.stock_PG4?.[tanggalKey];
            if (stockNode) {
                node.stock_warehouse_bpp = stockNode.values?.warehouse_bpp || '';
                node.stock_gd_mixer_fresh_pine = stockNode.values?.gd_mixer_fresh_pine || '';
                node.stock_gd_mat_banana = stockNode.values?.gd_mat_banana || '';
                node.stock_gd_mat_other_fruits = stockNode.values?.gd_mat_other_fruits || '';
                node.stock_quality_insp = stockNode.values?.quality_insp || '';
                node.stock_transit_and_transfer = stockNode.values?.transit_and_transfer || '';
                
                if (!node.last_imported_at) {
                    node.last_imported_at = stockNode.last_imported_at || '';
                }
            }
            
            updatedLoadedData.push(node);
        });
    });
    
    return updatedLoadedData;
};

function RealisasiStokBahanPG4() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk last update time
    const [lastUpdated, setLastUpdated] = useState(null);

    // Filter states
    const [filterItemCode, setFilterItemCode] = useState('');

    // State untuk data referensi Material Master
    const [materialMasterData, setMaterialMasterData] = useState({});

    // Fungsi untuk mengambil data Material Master dari Firebase
    const fetchMaterialMasterData = async () => {
        try {
            const materialMasterRef = ref(database, "db_inventory/db_master_data/tb_material_master");
            const materialMasterSnapshot = await onValuePromise(materialMasterRef);
            const materialMasterData = materialMasterSnapshot.val() || {};
            
            // Konversi ke object dengan kode_material sebagai key
            const materialMasterMap = {};
            Object.keys(materialMasterData).forEach(key => {
                const record = materialMasterData[key];
                const kodeMaterial = record.kode_material || key;
                materialMasterMap[kodeMaterial] = {
                    nama_produk: record.nama_produk || '',
                    deskripsi: record.deskripsi || ''
                };
            });
            
            setMaterialMasterData(materialMasterMap);
            return materialMasterMap;
        } catch (error) {
            console.error("Error fetching material master data:", error);
            return {};
        }
    };

    // Fungsi untuk mendapatkan nama produk berdasarkan item code
    const getNamaProdukByItemCode = (itemCode) => {
        if (!itemCode || !materialMasterData[itemCode]) {
            return '';
        }
        return materialMasterData[itemCode].nama_produk || '';
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Ambil data Material Master terlebih dahulu
                const materialMasterMap = await fetchMaterialMasterData();
                
                // Fetch data dari db_inventory/db_stok_bahan
                const stokRef = ref(database, "db_inventory/db_stok_bahan");
                const stokSnapshot = await onValuePromise(stokRef);
                const stokData = stokSnapshot.val();
                const loadedData = [];
                let latestUpdateTime = null;
                
                if (stokData) {
                    // Flatten data untuk ditampilkan dalam tabel
                    Object.keys(stokData).forEach(itemCode => {
                        const itemData = stokData[itemCode];
                        
                        if (!itemData || typeof itemData !== 'object') {
                            return;
                        }
                        
                        const rootItemCode = itemData.item_code || itemCode;
                        
                        // Ambil nama produk dari Material Master
                        const namaProduk = materialMasterMap[rootItemCode]?.nama_produk || '';
                        
                        // Gabungkan data dari realisasi, forecast_weekly, dan stock_PG4
                        const allNodes = {};
                        
                        // Kumpulkan semua tanggal
                        const collectDates = (source) => {
                            if (source) {
                                Object.keys(source).forEach(tanggalKey => {
                                    if (!allNodes[tanggalKey]) {
                                        allNodes[tanggalKey] = {
                                            tanggal_update: tanggalKey,
                                            item_code: rootItemCode,
                                            nama_produk: namaProduk, // Tambahkan nama produk
                                            firebaseKey: `${itemCode}_${tanggalKey}`
                                        };
                                    }
                                });
                            }
                        };
                        
                        collectDates(itemData.realisasi);
                        collectDates(itemData.forecast_weekly);
                        collectDates(itemData.stock_PG4);
                        
                        // Gabungkan data untuk setiap tanggal
                        Object.keys(allNodes).forEach(tanggalKey => {
                            const node = allNodes[tanggalKey];
                            
                            // Data dari realisasi
                            const realisasiNode = itemData.realisasi?.[tanggalKey];
                            if (realisasiNode) {
                                node.realisasi_fresh_pineapple = realisasiNode.values?.fresh_pineapple || '';
                                node.realisasi_banana = realisasiNode.values?.banana || '';
                                node.realisasi_other_fresh_fruits = realisasiNode.values?.other_fresh_fruits || '';
                                node.realisasi_rnd_fresh_pine = realisasiNode.values?.rnd_fresh_pine || '';
                                node.realisasi_rnd_banana = realisasiNode.values?.rnd_banana || '';
                                node.realisasi_rnd_other_fresh_fruits = realisasiNode.values?.rnd_other_fresh_fruits || '';
                                node.last_imported_at = realisasiNode.last_imported_at || '';
                            }
                            
                            // Data dari forecast_weekly
                            const forecastNode = itemData.forecast_weekly?.[tanggalKey];
                            if (forecastNode) {
                                node.forecast_weekly_01 = forecastNode.values?.['01'] || '';
                                node.forecast_weekly_02 = forecastNode.values?.['02'] || '';
                                node.forecast_weekly_03 = forecastNode.values?.['03'] || '';
                                node.forecast_weekly_04 = forecastNode.values?.['04'] || '';
                                node.forecast_weekly_05 = forecastNode.values?.['05'] || '';
                                node.forecast_weekly_06 = forecastNode.values?.['06'] || '';
                                node.forecast_weekly_07 = forecastNode.values?.['07'] || '';
                                node.forecast_weekly_08 = forecastNode.values?.['08'] || '';
                                node.forecast_weekly_09 = forecastNode.values?.['09'] || '';
                                
                                if (!node.last_imported_at) {
                                    node.last_imported_at = forecastNode.last_imported_at || '';
                                }
                            }
                            
                            // Data dari stock_PG4
                            const stockNode = itemData.stock_PG4?.[tanggalKey];
                            if (stockNode) {
                                node.stock_warehouse_bpp = stockNode.values?.warehouse_bpp || '';
                                node.stock_gd_mixer_fresh_pine = stockNode.values?.gd_mixer_fresh_pine || '';
                                node.stock_gd_mat_banana = stockNode.values?.gd_mat_banana || '';
                                node.stock_gd_mat_other_fruits = stockNode.values?.gd_mat_other_fruits || '';
                                node.stock_quality_insp = stockNode.values?.quality_insp || '';
                                node.stock_transit_and_transfer = stockNode.values?.transit_and_transfer || '';
                                
                                if (!node.last_imported_at) {
                                    node.last_imported_at = stockNode.last_imported_at || '';
                                }
                            }
                            
                            loadedData.push(node);
                            
                            // Cari waktu update terakhir
                            if (node.last_imported_at) {
                                const updateTime = new Date(node.last_imported_at);
                                if (!latestUpdateTime || updateTime > latestUpdateTime) {
                                    latestUpdateTime = updateTime;
                                }
                            }
                        });
                    });
                    
                    // Set last updated time
                    if (latestUpdateTime) {
                        setLastUpdated(latestUpdateTime);
                    }
                }

                // Urutkan berdasarkan item_code dan tanggal_update
                loadedData.sort((a, b) => {
                    if (a.item_code !== b.item_code) {
                        return a.item_code.localeCompare(b.item_code);
                    }
                    return b.tanggal_update.localeCompare(a.tanggal_update); // Terbaru di atas
                });

                setOriginalDataList(loadedData);
                setDataList(loadedData);

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data realisasi stok bahan.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                (item.item_code && item.item_code.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.nama_produk && item.nama_produk.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (item.tanggal_update && item.tanggal_update.toLowerCase().includes(searchQuery.toLowerCase()))
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handle refresh data setelah import
    const refreshDataAfterImport = async () => {
        try {
            // Refresh data Material Master terlebih dahulu
            const materialMasterMap = await fetchMaterialMasterData();
            
            const updatedData = await refreshRealisasiStokBahanData(database, onValuePromise);
            
            // Tambahkan nama produk ke setiap data
            const updatedDataWithProductName = updatedData.map(item => ({
                ...item,
                nama_produk: materialMasterMap[item.item_code]?.nama_produk || ''
            }));
            
            // Urutkan data
            updatedDataWithProductName.sort((a, b) => {
                if (a.item_code !== b.item_code) {
                    return a.item_code.localeCompare(b.item_code);
                }
                return b.tanggal_update.localeCompare(a.tanggal_update);
            });
            
            return updatedDataWithProductName;
        } catch (error) {
            console.error("Error refreshing data:", error);
            throw error;
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            setSnackbar({
                open: true,
                message: "Tidak ada file yang dipilih.",
                severity: "warning",
            });
            return;
        }

        setImportLoading(true);
        setSnackbar({
            open: true,
            message: "Memulai proses import...",
            severity: "info",
        });

        try {
            // Gunakan fungsi import khusus untuk realisasi stok bahan
            const result = await importExcelToFirebaseRealisasiStokBahan(file, database, onValuePromise);
            
            if (result.success) {
                setSnackbar({
                    open: true,
                    message: `Impor selesai! ${result.importedCount} data baru, ${result.updatedCount} data diperbarui. Tanggal update: ${result.tanggalUpdate}`,
                    severity: "success",
                });
                
                // Set last updated time
                setLastUpdated(new Date());
                
                // Refresh data setelah import berhasil
                const finalData = await refreshDataAfterImport();
                
                setDataList(finalData);
                setOriginalDataList(finalData);
            }
        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: error.error || "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            setImportLoading(false);
        }
    };

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterItemCode) {
            filtered = filtered.filter(item => item.item_code === filterItemCode);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterItemCode('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    // Get unique item codes for filter dropdown
    const itemCodeList = [...new Set(originalDataList.map(item => item.item_code))].filter(Boolean).sort();

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/*<RimenderStock />*/}

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Item Code</InputLabel>
                            <Select
                                value={filterItemCode}
                                label="Item Code"
                                onChange={(e) => setFilterItemCode(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {itemCodeList.map((itemCode) => (
                                    <MenuItem key={itemCode} value={itemCode}>
                                        {itemCode}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Realisasi dan Stok Bahan PG4
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari item code, nama produk, atau tanggal..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 300 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        {/* Gunakan CustomTableContainer di sini */}
                        <CustomTableContainer
                            sx={{ 
                                maxHeight: 500,
                                border: '1px solid #e0e0e0',
                                borderRadius: 1
                            }}
                            hoverScrollbar={true}
                            scrollbarHeight="8px"
                            scrollbarWidth="8px"
                            scrollbarThumbColor="#c1c1c1"
                            scrollbarHoverColor="#a8a8a8"
                        >
                            <Table stickyHeader aria-label="realisasi stok bahan table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch" rowSpan={2}>Item Code</TableCell>
                                        <TableCell className="ded-tch" rowSpan={2}>Nama Produk</TableCell> {/* Kolom baru */}
                                        <TableCell className="ded-tch" rowSpan={2}>Tanggal<br/>Update</TableCell>
                                        
                                        {/* Realisasi */}
                                        <TableCell className="ded-tch" colSpan={6} align="center">
                                            Realisasi
                                        </TableCell>
                                        
                                        {/* Forecast Weekly */}
                                        <TableCell className="ded-tch" colSpan={9} align="center">
                                            Forecast Weekly
                                        </TableCell>
                                        
                                        {/* Stock PG4 */}
                                        <TableCell className="ded-tch" colSpan={6} align="center">
                                            Stock PG4
                                        </TableCell>
                                    </TableRow>
                                    
                                    <TableRow className="tr">
                                        {/* Realisasi - Produksi */}
                                        <TableCell className="ded-tc">Fresh<br/>Pineapple</TableCell>
                                        <TableCell className="ded-tc">Banana</TableCell>
                                        <TableCell className="ded-tc">Other Fresh<br/>Fruits</TableCell>
                                        
                                        {/* Realisasi - R&D */}
                                        <TableCell className="ded-tc">R&D<br/>Fresh Pine</TableCell>
                                        <TableCell className="ded-tc">R&D<br/>Banana</TableCell>
                                        <TableCell className="ded-tc">R&D<br/>Other Fresh Fruits</TableCell>
                                        
                                        {/* Forecast Weekly */}
                                        <TableCell className="ded-tc">01</TableCell>
                                        <TableCell className="ded-tc">02</TableCell>
                                        <TableCell className="ded-tc">03</TableCell>
                                        <TableCell className="ded-tc">04</TableCell>
                                        <TableCell className="ded-tc">05</TableCell>
                                        <TableCell className="ded-tc">06</TableCell>
                                        <TableCell className="ded-tc">07</TableCell>
                                        <TableCell className="ded-tc">08</TableCell>
                                        <TableCell className="ded-tc">09</TableCell>
                                        
                                        {/* Stock PG4 */}
                                        <TableCell className="ded-tc">Warehouse<br/>BPP</TableCell>
                                        <TableCell className="ded-tc">GD Mixer<br/>Fresh Pine</TableCell>
                                        <TableCell className="ded-tc">GD Mat<br/>Banana</TableCell>
                                        <TableCell className="ded-tc">GD Mat<br/>Other Fruits</TableCell>
                                        <TableCell className="ded-tc">Quality<br/>Insp</TableCell>
                                        <TableCell className="ded-tc">Transit &<br/>Transfer</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {displayedDataList
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => (
                                            <TableRow key={row.firebaseKey}>
                                                <TableCell className="ded-tbtrtc">
                                                    <Box>
                                                        <div style={{ fontWeight: 'bold' }}>
                                                            {formatText(row.item_code)}
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <div>
                                                            {formatText(row.nama_produk)}
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{formatText(row.tanggal_update)}</TableCell>
                                                
                                                {/* Realisasi - Produksi */}
                                                <TableCell>{formatNumber(row.realisasi_fresh_pineapple)}</TableCell>
                                                <TableCell>{formatNumber(row.realisasi_banana)}</TableCell>
                                                <TableCell>{formatNumber(row.realisasi_other_fresh_fruits)}</TableCell>
                                                
                                                {/* Realisasi - R&D */}
                                                <TableCell>{formatNumber(row.realisasi_rnd_fresh_pine)}</TableCell>
                                                <TableCell>{formatNumber(row.realisasi_rnd_banana)}</TableCell>
                                                <TableCell>{formatNumber(row.realisasi_rnd_other_fresh_fruits)}</TableCell>
                                                
                                                {/* Forecast Weekly */}
                                                <TableCell>{formatNumber(row.forecast_weekly_01)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_02)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_03)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_04)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_05)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_06)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_07)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_08)}</TableCell>
                                                <TableCell>{formatNumber(row.forecast_weekly_09)}</TableCell>
                                                
                                                {/* Stock PG4 */}
                                                <TableCell>{formatNumber(row.stock_warehouse_bpp)}</TableCell>
                                                <TableCell>{formatNumber(row.stock_gd_mixer_fresh_pine)}</TableCell>
                                                <TableCell>{formatNumber(row.stock_gd_mat_banana)}</TableCell>
                                                <TableCell>{formatNumber(row.stock_gd_mat_other_fruits)}</TableCell>
                                                <TableCell>{formatNumber(row.stock_quality_insp)}</TableCell>
                                                <TableCell>{formatNumber(row.stock_transit_and_transfer)}</TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CustomTableContainer>
                        
                        {/* Container untuk TablePagination dan Last Updated Info */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            {/* Bagian kiri: Info Data diupdate terakhir */}
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                Data diupdate terakhir: {formatLastUpdated(lastUpdated)}
                            </Typography>
                            
                            {/* Bagian kanan: TablePagination */}
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={displayedDataList.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                sx={{ 
                                    flexShrink: 0,
                                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                        margin: 0
                                    }
                                }}
                            />
                        </Box>
                    </>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        minHeight: 200,
                        border: '1px dashed #e0e0e0',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data realisasi stok bahan.'}
                        </Typography>
                    </Box>
                )}
            </Paper>
{/*  */}
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default RealisasiStokBahanPG4;